<template>
  <div class="page-table-layout accruals-configuration-page" data-testid="accruals-configuration-page">
    <v-card class="all-width flex-card shadow-off">
      <v-card-text class="page-table-layout__body accruals-configuration-page__body">
        <accruals-workspace-header
          title="Configuration"
          subtitle="Configure rules and conditions to automate accrual estimation."
          @back="goBack"
        />

        <div class="accruals-configuration-page__tabs-row">
          <accruals-configuration-tabs
            v-model="activeTab"
            :disabled="tabContentLoading"
          />
          <div
            v-if="isEstimationTab && hasPersistedEstimationRules && canConfigure"
            class="accruals-configuration-page__run-cluster"
          >
            <v-btn
              class="primaryBtn accruals-configuration-page__run-btn"
              small
              :loading="computationRunBusy"
              :disabled="computationRunBusy"
              data-testid="accruals-configuration-page__run-estimates-btn"
              @click="onRunEstimates"
            >
              Run estimates
            </v-btn>
            <span
              v-if="computationRunMeta"
              class="accruals-configuration-page__run-meta"
              data-testid="accruals-configuration-page__run-estimates-meta"
            >
              {{ computationRunMeta }}
            </span>
          </div>
        </div>

        <section
          v-if="tabContentLoading"
          class="accruals-rules-loading"
          data-testid="accruals-configuration-page__loading"
        >
          <article class="accruals-rules-loading__card">
            <v-skeleton-loader
              type="heading"
              class="accruals-rules-loading__heading"
            />
            <v-skeleton-loader type="list-item-two-line" />
            <v-skeleton-loader type="list-item-two-line" />
            <v-skeleton-loader type="list-item-two-line" />
          </article>
        </section>

        <accruals-rules-empty-state
          v-else-if="viewMode === 'empty'"
          :tab-id="activeTab"
          :show-actions="canConfigure"
          @import="onImport"
          @add-rule="onAddRuleFromEmpty"
        />

        <accruals-estimation-rules-editor
          v-else-if="isEstimationTab"
          :rules="estimationRules"
          :selected-rule-id="selectedEstimationRuleId"
          :saving="computationRuleSaving"
          :read-only="!canConfigure"
          :templates="computationTemplates"
          data-testid="accruals-configuration-page__estimation-rules-editor"
          @select-rule="onSelectEstimationRule"
          @add-rule="onAddEstimationRule"
          @update-rules="onUpdateEstimationRules"
          @update-rule="onUpdateEstimationRuleDraft"
          @save-rule="onSaveEstimationRule"
          @save-rules="onSaveEstimationRules"
          @delete-rule="onDeleteEstimationRule"
          @cancel-edit="onCancelEstimationRuleEdit"
        />

        <template v-else-if="isInfoRequestTab">
          <article
            class="accruals-configuration-page__im-card"
            data-testid="accruals-configuration-page__im-card"
          >
            <header class="accruals-configuration-page__im-card-header">
              <div class="accruals-configuration-page__im-card-heading">
                <h2 class="accruals-configuration-page__im-card-title">Info Request Rules</h2>
                <p class="accruals-configuration-page__im-card-subtitle">{{ imCardSubtitle }}</p>
              </div>

              <div
                v-if="imBuilderEditing && canConfigure"
                class="accruals-configuration-page__im-card-actions"
              >
                <base-button
                  secondary
                  label="Cancel"
                  class="accruals-configuration-page__im-cancel-btn"
                  :disabled="imRuleSaving"
                  data-testid="accruals-configuration-page__im-cancel-btn"
                  @click="onImBuilderCancel"
                />
                <base-button
                  primary
                  label="Save"
                  class="accruals-configuration-page__im-save-btn"
                  :loading="imRuleSaving"
                  :disabled="imRuleSaving"
                  data-testid="accruals-configuration-page__im-save-btn"
                  @click="onImBuilderSave"
                />
              </div>

              <base-button
                v-else-if="canConfigure"
                secondary
                label="Edit"
                class="accruals-configuration-page__im-edit-btn"
                :image="imCardAssets.ICON_EDIT"
                image-alt=""
                data-testid="accruals-configuration-page__im-edit-btn"
                @click="onImBuilderEdit"
              />
            </header>

            <div class="accruals-configuration-page__im-card-divider" />

            <div class="accruals-configuration-page__im-card-body">
              <div
                class="accruals-configuration-page__im-actions"
                data-testid="accruals-configuration-page__im-actions"
              >
                <v-btn
                  v-if="canConfigure"
                  class="secondaryBtn"
                  :loading="imPreviewLoading"
                  :disabled="!canPreviewImRule"
                  data-testid="accruals-configuration-page__preview-btn"
                  @click="previewInfoRequestRule"
                >
                  Preview
                </v-btn>
                <v-btn
                  v-if="canConfigure"
                  class="primaryBtn"
                  :loading="imSelectionBusy"
                  :disabled="!canRunImSelection"
                  data-testid="accruals-configuration-page__start-selection-btn"
                  @click="runInfoRequestSelection"
                >
                  Start selection
                </v-btn>
                <span
                  v-if="imPreview"
                  class="accruals-configuration-page__im-preview-meta"
                  data-testid="accruals-configuration-page__im-preview-meta"
                >
                  Matched {{ imPreview.match_count || 0 }}
                  <template v-if="imPreview.openPoSnapshotVersionId">
                    · snapshot {{ imPreview.openPoSnapshotVersionId }}
                  </template>
                </span>
                <span
                  v-if="imSelectionRunMeta"
                  class="accruals-configuration-page__im-run-meta"
                  data-testid="accruals-configuration-page__im-run-meta"
                >
                  {{ imSelectionRunMeta }}
                </span>
              </div>

              <div
                v-if="imPreviewQueryText"
                class="accruals-configuration-page__im-query"
                data-testid="accruals-configuration-page__im-query"
              >
                <div class="accruals-configuration-page__im-query-header">
                  <span class="accruals-configuration-page__im-query-title">Mongo query</span>
                  <span class="accruals-configuration-page__im-query-hint">
                    open_po · same filter used for match count
                  </span>
                </div>
                <pre class="accruals-configuration-page__im-query-body">{{ imPreviewQueryText }}</pre>
              </div>

              <accruals-im-groups-builder
                embedded
                :groups="imGroups"
                :group-logic="imGroupLogic"
                :group-connectors="imGroupConnectors"
                :editing="imBuilderEditing && canConfigure"
                :editing-group-id="editingGroupId"
                :editing-index="editingIndex"
                :saving="imRuleSaving"
                :condition-errors="conditionErrors"
                @add-group="addImGroup"
                @remove-group="removeImGroup"
                @set-group-logic="setImGroupLogic"
                @set-group-connector="setImGroupConnector"
                @add-condition="addImCondition"
                @update-condition="updateImCondition"
                @delete-condition="deleteImCondition"
                @edit-condition="onEditImCondition"
                @confirm-condition="onConfirmImCondition"
              />
            </div>
          </article>
        </template>

        <accruals-rules-builder
          v-else
          :tab-id="activeTab"
          :conditions="activeConditions"
          :editing-index="editingIndex"
          :saving="false"
          :condition-errors="conditionErrors"
          @edit="onBuilderEdit"
          @cancel="onBuilderCancel"
          @save="onBuilderSave"
          @add-condition="addCondition"
          @update-condition="updateCondition"
          @update-rule-meta="updateRuleMeta"
          @delete-condition="deleteCondition"
        />
      </v-card-text>
    </v-card>

    <lb-dialog
      v-model="saveConfirmDialog"
      heading="Re-running will reset affected estimates"
      width="520"
      :loading="computationRuleSaving || imRuleSaving"
      data-testid="accruals-configuration-page__save-confirm-dialog"
    >
      <template #body>
        <p>
          This change affects vendor(s)/PO(s) already in this cycle. Estimates already
          finalised, and Information Requests already raised, against the affected
          population will be reset for re-evaluation. This cannot be undone.
        </p>
        <p v-if="pendingSaveAffectedCount != null">
          {{ pendingSaveAffectedCount }} line{{ pendingSaveAffectedCount === 1 ? '' : 's' }}
          will be reset.
        </p>
      </template>
      <template #actions>
        <base-button
          label="Cancel"
          secondary
          :disabled="computationRuleSaving || imRuleSaving"
          data-testid="accruals-configuration-page__save-confirm-cancel"
          @click="onCancelSaveConfirm"
        />
        <base-button
          label="Continue and re-run"
          primary
          class="ml-2"
          :loading="computationRuleSaving || imRuleSaving"
          :disabled="computationRuleSaving || imRuleSaving"
          data-testid="accruals-configuration-page__save-confirm-submit"
          @click="onConfirmSaveEstimationRule"
        />
      </template>
    </lb-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import AccrualsWorkspaceHeader from './components/AccrualsWorkspaceHeader.vue';
import AccrualsConfigurationTabs from './components/AccrualsConfigurationTabs.vue';
import AccrualsRulesEmptyState from './components/AccrualsRulesEmptyState.vue';
import AccrualsRulesBuilder from './components/AccrualsRulesBuilder.vue';
import AccrualsEstimationRulesEditor from './components/AccrualsEstimationRulesEditor.vue';
import AccrualsImGroupsBuilder from './components/AccrualsImGroupsBuilder.vue';
import {
  ACCRUALS_CONFIG_ASSETS,
  ACCRUALS_CONFIG_TABS,
  createEmptyCondition,
  createEmptyImGroup,
  createInitialImGroups,
  IM_GROUP_LOGIC,
  RULES_BUILDER_SUBTITLE,
} from './accrualsConfigurationConstants';
import { createEmptyEstimationRule, ESTIMATION_ALL_VENDORS_ID } from './accrualsEstimationRulesConstants';
import {
  apiRuleToUi,
  estimationRuleId,
  isEstimationDraftRule,
  normalizeEstimationRule,
  uiRuleToApi,
  uniqueEstimationRules,
} from './accrualsEstimationRuleUtils';
import {
  CONDITIONS_INCOMPLETE_MESSAGE,
  validateCondition,
  validateConditions,
  validateImGroups,
  validateOwnerId,
} from './accrualsConditionValidation';
import {
  createInitialBuilderConditions,
} from './accrualsConfigurationStorage';
import {
  createEmptyRuleMeta,
  normalizeRuleMeta,
} from './accrualsRulesBuilderUtils';
import { ACCRUALS_ROUTES } from './accrualsPageConstants';
import {
  formatComputationRunMeta,
  formatImSelectionRunMeta,
  isAccrualsComputationInProgress,
  isAccrualsImSelectionInProgress,
  pollAccrualsRun,
} from './accrualsRunPoll';

export default {
  name: 'AccrualsConfiguration',
  components: {
    AccrualsWorkspaceHeader,
    AccrualsConfigurationTabs,
    AccrualsRulesEmptyState,
    AccrualsRulesBuilder,
    AccrualsEstimationRulesEditor,
    AccrualsImGroupsBuilder,
  },
  data() {
    return {
      activeTab: ACCRUALS_CONFIG_TABS.ESTIMATION,
      builderActiveByTab: {
        [ACCRUALS_CONFIG_TABS.ESTIMATION]: false,
        [ACCRUALS_CONFIG_TABS.INFO_REQUEST]: false,
      },
      rulesByTab: {
        [ACCRUALS_CONFIG_TABS.ESTIMATION]: [],
        [ACCRUALS_CONFIG_TABS.INFO_REQUEST]: [],
      },
      selectedEstimationRuleId: '',
      estimationLocalDrafts: [],
      estimationEditsById: {},
      saveConfirmDialog: false,
      pendingSaveRule: null,
      pendingSaveQueue: [],
      pendingSaveKind: 'computation',
      pendingImAction: '',
      pendingSaveAffectedCount: null,
      rulesMetaByTab: {},
      builderEditing: false,
      builderDraft: null,
      persistEnabled: true,
      ownerMode: 'open_po_field',
      ownerModeItems: [
        {
          value: 'open_po_field',
          text: 'Open PO approver email',
        },
        {
          value: 'cycle_default',
          text: 'Default info owner id',
        },
      ],
      defaultInfoOwnerId: '',
      ownerError: '',
      editingGroupId: '',
      editingIndex: -1,
      conditionErrors: {},
      imGroups: [],
      imGroupLogic: IM_GROUP_LOGIC.AND,
      imGroupConnectors: [],
      imRuleLoadPromise: null,
      imBuilderEditing: false,
      imCardAssets: ACCRUALS_CONFIG_ASSETS,
      computationRunStarting: false,
      imSelectionPolling: false,
      computationRunPolling: false,
      runPollGeneration: 0,
    };
  },
  computed: {
    ...mapState('accruals', {
      imRule: (s) => s.imRule,
      imRuleSaving: (s) => s.imRuleSaving,
      imRuleLoading: (s) => s.imRuleLoading,
      imPreview: (s) => s.imPreview,
      imPreviewLoading: (s) => s.imPreviewLoading,
      imSelection: (s) => s.imSelection,
      imSelectionLoading: (s) => s.imSelectionLoading,
      computationRules: (s) => s.computationRules,
      computationTemplates: (s) => s.computationTemplates,
      computationRuleLoading: (s) => s.computationRuleLoading,
      computationRuleSaving: (s) => s.computationRuleSaving,
      computationRun: (s) => s.computationRun,
      access: (s) => s.access,
    }),
    projectId() {
      return this.$route.params.id;
    },
    canConfigure() {
      return Boolean(this.access?.canConfigure);
    },
    isInfoRequestTab() {
      return this.activeTab === ACCRUALS_CONFIG_TABS.INFO_REQUEST;
    },
    isEstimationTab() {
      return this.activeTab === ACCRUALS_CONFIG_TABS.ESTIMATION;
    },
    estimationRules() {
      const persisted = (this.computationRules || []).map((rule) => {
        const mapped = apiRuleToUi(rule);
        const id = estimationRuleId(mapped);
        return (id && this.estimationEditsById[id]) ? this.estimationEditsById[id] : mapped;
      });
      return [...this.estimationLocalDrafts, ...persisted];
    },
    hasPersistedEstimationRules() {
      return (this.computationRules || []).length > 0;
    },
    activeConditions() {
      if (this.isEstimationTab) return [];
      return this.rulesByTab[this.activeTab] || [];
    },
    activeRuleMeta() {
      return normalizeRuleMeta(
        this.rulesMetaByTab?.[this.activeTab] || {},
        this.activeConditions.length,
      );
    },
    builderSaving() {
      return this.isInfoRequestTab && this.imRuleSaving;
    },
    tabContentLoading() {
      return (this.isInfoRequestTab && this.imRuleLoading)
        || (this.isEstimationTab && this.computationRuleLoading);
    },
    viewMode() {
      if (this.isEstimationTab) {
        return this.estimationRules.length ? 'editor' : 'empty';
      }
      if (this.isInfoRequestTab) {
        if (this.imGroups.length > 0) return 'builder';
        if (this.builderActiveByTab[ACCRUALS_CONFIG_TABS.INFO_REQUEST]) return 'builder';
        return 'empty';
      }
      return 'empty';
    },
    imValidation() {
      return validateImGroups(this.imGroups);
    },
    canSaveImRule() {
      return (
        this.isInfoRequestTab &&
        this.imValidation.valid &&
        !this.imRuleSaving
      );
    },
    canPreviewImRule() {
      return (
        this.isInfoRequestTab
        && this.imValidation.valid
        && !this.imPreviewLoading
        && !this.imRuleSaving
      );
    },
    canRunImSelection() {
      return (
        this.isInfoRequestTab &&
        this.imPreview?.rule_version > 0 &&
        this.imPreview?.openPoSnapshotVersionId &&
        !this.imSelectionBusy
      );
    },
    imSelectionBusy() {
      return this.imSelectionLoading
        || this.imSelectionPolling
        || isAccrualsImSelectionInProgress(this.imSelection?.status);
    },
    imSelectionRunMeta() {
      return formatImSelectionRunMeta(this.imSelection);
    },
    computationRunBusy() {
      return this.computationRunStarting
        || this.computationRunPolling
        || isAccrualsComputationInProgress(this.computationRun?.status);
    },
    computationRunMeta() {
      return formatComputationRunMeta(this.computationRun);
    },
    imPreviewQueryText() {
      const query = this.imPreview?.query;
      if (!query || typeof query !== 'object') return '';
      try {
        return JSON.stringify(query, null, 2);
      } catch (err) {
        console.log('[AccrualsConfiguration]: imPreviewQueryText failed', err);
        return '';
      }
    },
    imCardSubtitle() {
      return RULES_BUILDER_SUBTITLE;
    },
  },
  watch: {
    '$route.query.tab'(tab) {
      this.syncTabFromRoute(tab);
    },
    '$route.query.view'(view) {
      if (view === 'builder') {
        this.openBuilder();
      }
    },
    activeTab(tab, previousTab) {
      if (tab === previousTab) return;
      this.resetBuilderEditState();
      if (this.$route.query.tab !== tab) {
        this.$nova.gotoLink({
          path: this.$route.path,
          addquery: { tab },
        });
      }
      if (tab === ACCRUALS_CONFIG_TABS.INFO_REQUEST) {
        this.loadInfoRequestRule();
      } else if (tab === ACCRUALS_CONFIG_TABS.ESTIMATION) {
        this.loadEstimationRulesFromApi();
      }
    },
    computationRules() {
      if (!this.isEstimationTab) return;
      if (this.selectedEstimationRuleId && this.estimationRules.some(
        (rule) => estimationRuleId(rule) === this.selectedEstimationRuleId,
      )) {
        return;
      }
      const first = this.estimationRules[0];
      this.selectedEstimationRuleId = first ? estimationRuleId(first) : '';
    },
  },
  created() {
    this.syncTabFromRoute(this.$route.query.tab);
    if (this.$route.query.view === 'builder') {
      this.openBuilder();
    }
    this.bootstrapTabData();
  },
  beforeDestroy() {
    this.runPollGeneration += 1;
    this.imSelectionPolling = false;
    this.computationRunPolling = false;
    this.resetRowEditState();
    this.defaultInfoOwnerId = '';
    this.ownerError = '';
    this.ownerMode = 'open_po_field';
    this.imGroups = [];
    this.imGroupLogic = IM_GROUP_LOGIC.AND;
    this.imGroupConnectors = [];
    this.estimationLocalDrafts = [];
    this.estimationEditsById = {};
    this.pendingSaveQueue = [];
    this.$store.commit('accruals/setSelectedRuleId', null);
  },
  methods: {
    resetRowEditState() {
      this.editingGroupId = '';
      this.editingIndex = -1;
      this.conditionErrors = {};
    },
    syncImGroupConnectors(preferredLogic) {
      const needed = Math.max(0, this.imGroups.length - 1);
      const fallback = preferredLogic === 'OR' ? IM_GROUP_LOGIC.OR : IM_GROUP_LOGIC.AND;
      const next = [];
      for (let i = 0; i < needed; i += 1) {
        const existing = this.imGroupConnectors[i];
        next.push(existing === 'OR' || existing === 'AND' ? existing : fallback);
      }
      this.imGroupConnectors = next;
      this.imGroupLogic = next.length && next.every((c) => c === next[0])
        ? next[0]
        : (next[0] || IM_GROUP_LOGIC.AND);
    },

    resetBuilderEditState() {
      this.resetRowEditState();
    },
    validateOwnerField() {
      if (this.ownerMode === 'open_po_field') {
        this.ownerError = '';
        return true;
      }
      this.ownerError = validateOwnerId(this.defaultInfoOwnerId);
      return !this.ownerError;
    },
    getRuleMetaForTab(tabId, conditionCount = 0) {
      return normalizeRuleMeta(this.rulesMetaByTab?.[tabId] || {}, conditionCount);
    },
    setRuleMetaForTab(tabId, meta, conditionCount = 0) {
      this.$set(
        this.rulesMetaByTab,
        tabId,
        normalizeRuleMeta(meta, conditionCount),
      );
    },
    syncRuleMetaLength(tabId) {
      const conditions = this.rulesByTab[tabId] || [];
      this.setRuleMetaForTab(tabId, this.getRuleMetaForTab(tabId, conditions.length), conditions.length);
    },
    syncTabFromRoute(tab) {
      const nextTab = tab === ACCRUALS_CONFIG_TABS.INFO_REQUEST
        ? ACCRUALS_CONFIG_TABS.INFO_REQUEST
        : ACCRUALS_CONFIG_TABS.ESTIMATION;
      if (nextTab === this.activeTab) return;
      this.activeTab = nextTab;
    },
    bootstrapTabData() {
      if (this.projectId) {
        this.$store.dispatch('accruals/loadAccess', { projectId: this.projectId });
      }
      this.$store.dispatch('accruals/loadComputationTemplates', { enabled: true });
      if (this.isInfoRequestTab) {
        this.loadInfoRequestRule();
      } else if (this.isEstimationTab) {
        this.loadEstimationRulesFromApi();
      }
    },
    async loadEstimationRulesFromApi() {
      if (!this.projectId) return;
      try {
        const rules = await this.$store.dispatch('accruals/loadComputationRules', {
          projectId: this.projectId,
        });
        this.estimationEditsById = {};
        if (!this.selectedEstimationRuleId && rules && rules[0]) {
          this.selectedEstimationRuleId = String(rules[0]._id);
        }
        await this.refreshComputationRunStatus();
      } catch (err) {
        console.log('[AccrualsConfiguration]: loadComputationRules failed', err);
      }
    },
    isDraftEstimationRule(rule) {
      return isEstimationDraftRule(rule);
    },
    upsertLocalDraft(rule) {
      const id = estimationRuleId(rule);
      const next = this.estimationLocalDrafts.slice();
      const index = next.findIndex((item) => estimationRuleId(item) === id);
      if (index >= 0) next.splice(index, 1, rule);
      else next.push(rule);
      this.estimationLocalDrafts = next;
    },
    removeLocalDraft(ruleId) {
      this.estimationLocalDrafts = this.estimationLocalDrafts.filter(
        (item) => estimationRuleId(item) !== String(ruleId),
      );
    },
    mapApiConditionsToUi(conditions) {
      return (conditions || []).map((c, index) => ({
        id: `condition-${c.field || 'x'}-${index}`,
        field: c.field || '',
        operator: c.op || c.operator || 'eq',
        value: c.value === null || c.value === undefined ? '' : String(c.value),
      }));
    },
    mapApiGroupsToUi(data) {
      if (Array.isArray(data?.groups) && data.groups.length) {
        return data.groups.map((group, gIdx) => ({
          id: group.id || `group-${gIdx + 1}`,
          logic: (group.logic || IM_GROUP_LOGIC.AND).toUpperCase() === 'OR'
            ? IM_GROUP_LOGIC.OR
            : IM_GROUP_LOGIC.AND,
          conditions: this.mapApiConditionsToUi(group.conditions),
        }));
      }
      const flat = this.mapApiConditionsToUi(data?.conditions);
      if (!flat.length) return [];
      return [
        {
          id: 'legacy-and',
          logic: IM_GROUP_LOGIC.AND,
          conditions: flat,
        },
      ];
    },
    collectImGroupErrors() {
      const { valid, errorsByKey } = validateImGroups(this.imGroups);
      this.conditionErrors = errorsByKey;
      return valid;
    },
    async loadInfoRequestRule() {
      if (!this.projectId) return null;
      if (this.imRuleLoadPromise) {
        return this.imRuleLoadPromise;
      }

      this.imRuleLoadPromise = this.fetchInfoRequestRule();
      try {
        return await this.imRuleLoadPromise;
      } finally {
        this.imRuleLoadPromise = null;
      }
    },
    async fetchInfoRequestRule() {
      try {
        const data = await this.$store.dispatch('accruals/loadImRule', {
          projectId: this.projectId,
        });
        this.persistEnabled = false;
        this.resetRowEditState();
        const groups = this.mapApiGroupsToUi(data);
        if (groups.length && data?.status !== 'disabled') {
          this.imGroups = groups;
          const fallbackLogic =
            String(data?.group_logic || data?.logic || 'AND').toUpperCase() === 'OR'
              ? IM_GROUP_LOGIC.OR
              : IM_GROUP_LOGIC.AND;
          const apiConnectors = Array.isArray(data?.group_connectors)
            ? data.group_connectors
            : [];
          this.imGroupConnectors = groups.slice(0, -1).map((_, idx) => (
            String(apiConnectors[idx] || fallbackLogic).toUpperCase() === 'OR'
              ? IM_GROUP_LOGIC.OR
              : IM_GROUP_LOGIC.AND
          ));
          this.imGroupLogic = fallbackLogic;
          this.$set(this.builderActiveByTab, ACCRUALS_CONFIG_TABS.INFO_REQUEST, true);
          this.imBuilderEditing = false;
          this.setRuleMetaForTab(
            ACCRUALS_CONFIG_TABS.INFO_REQUEST,
            this.getRuleMetaForTab(ACCRUALS_CONFIG_TABS.INFO_REQUEST, data.conditions.length),
            data.conditions.length,
          );
        } else {
          this.imGroups = [];
          this.imGroupLogic = IM_GROUP_LOGIC.AND;
          this.imGroupConnectors = [];
          this.$set(this.builderActiveByTab, ACCRUALS_CONFIG_TABS.INFO_REQUEST, false);
          this.imBuilderEditing = false;
        }
        await this.refreshImSelectionStatus();
      } catch (err) {
        console.log('[AccrualsConfiguration]: loadImRule failed', err);
        return null;
      } finally {
        this.$nextTick(() => {
          this.persistEnabled = true;
        });
      }
    },
    buildOwnerResolution() {
      return {
        mode: 'open_po_field',
        map_field: 'approver_email',
        default_info_owner_id: null,
      };
    },
    async saveInfoRequestRule({ confirm = false } = {}) {
      if (!this.collectImGroupErrors()) {
        this.$store.commit('sbError', CONDITIONS_INCOMPLETE_MESSAGE, { root: true });
        return false;
      }
      if (!this.validateOwnerField()) return false;
      try {
        const result = await this.$store.dispatch('accruals/saveImRule', {
          projectId: this.projectId,
          groups: this.imGroups,
          groupLogic: this.imGroupLogic,
          groupConnectors: this.imGroupConnectors,
          ownerResolution: this.buildOwnerResolution(),
          confirm,
        });
        if (result && result.requires_confirmation) {
          this.pendingSaveKind = 'im';
          this.pendingSaveAffectedCount = result.affected_count;
          this.saveConfirmDialog = true;
          return false;
        }
        this.resetBuilderEditState();
        return true;
      } catch (err) {
        console.log('[AccrualsConfiguration]: saveImRule failed', err);
        return false;
      }
    },
    async disableInfoRequestRule() {
      const groups = this.mapApiGroupsToUi(this.imRule);
      const { valid, flatConditions } = validateImGroups(groups);
      if (!valid || !flatConditions.length) {
        this.$store.commit('accruals/setImRule', {
          ...(this.imRule || {}),
          groups: [],
          conditions: [],
          status: 'disabled',
        });
        return true;
      }
      try {
        await this.$store.dispatch('accruals/saveImRule', {
          projectId: this.projectId,
          groups,
          groupLogic: this.imRule?.group_logic || IM_GROUP_LOGIC.AND,
          groupConnectors: this.imRule?.group_connectors || [],
          ownerResolution: this.buildOwnerResolution(),
          status: 'disabled',
        });
        return true;
      } catch (err) {
        console.log('[AccrualsConfiguration]: disableInfoRequestRule failed', err);
        return false;
      }
    },
    async loadImPreview() {
      await this.$store.dispatch('accruals/previewImRule', {
        projectId: this.projectId,
        ruleVersion: this.imRule?.version,
      });
    },
    async previewInfoRequestRule() {
      try {
        const saved = await this.saveInfoRequestRule();
        if (!saved) {
          if (this.saveConfirmDialog) this.pendingImAction = 'preview';
          return;
        }
        await this.loadImPreview();
      } catch (err) {
        console.log('[AccrualsConfiguration]: previewImRule failed', err);
      }
    },
    async runInfoRequestSelection() {
      try {
        const saved = await this.saveInfoRequestRule();
        if (!saved) {
          if (this.saveConfirmDialog) this.pendingImAction = 'selection';
          return;
        }
        await this.startImSelectionRun();
      } catch (err) {
        console.log('[AccrualsConfiguration]: startImSelection failed', err);
      }
    },
    async startImSelectionRun() {
      const result = await this.$store.dispatch('accruals/startImSelection', {
        projectId: this.projectId,
        ruleVersion: this.imRule?.version,
      });
      if (result?.skipped) {
        await this.pollImSelectionUntilSettled();
        return;
      }
      await this.pollImSelectionUntilSettled(result?.selection_run_id);
    },
    async refreshImSelectionStatus() {
      if (!this.projectId) return null;
      try {
        const data = await this.$store.dispatch('accruals/loadImSelectionStatus', {
          projectId: this.projectId,
        });
        if (isAccrualsImSelectionInProgress(data?.status)) {
          this.pollImSelectionUntilSettled(data?.selection_run_id);
        }
        return data;
      } catch (err) {
        console.log('[AccrualsConfiguration]: loadImSelectionStatus failed', err);
        return null;
      }
    },
    async pollImSelectionUntilSettled(selectionRunId) {
      const generation = this.runPollGeneration;
      this.imSelectionPolling = true;
      try {
        const last = await pollAccrualsRun({
          fetchStatus: () => this.$store.dispatch('accruals/loadImSelectionStatus', {
            projectId: this.projectId,
            selectionRunId,
          }),
          isInProgress: isAccrualsImSelectionInProgress,
          shouldStop: () => generation !== this.runPollGeneration,
        });
        if (generation !== this.runPollGeneration) return last;
        this.notifyImSelectionSettled(last);
        return last;
      } catch (err) {
        console.log('[AccrualsConfiguration]: pollImSelectionUntilSettled failed', err);
        return null;
      } finally {
        if (generation === this.runPollGeneration) this.imSelectionPolling = false;
      }
    },
    notifyImSelectionSettled(run) {
      if (isAccrualsImSelectionInProgress(run?.status) || !run?.status) return;
      if (run.status === 'failed') {
        this.$store.commit('sbError', run.error || 'IM selection failed', { root: true });
        return;
      }
      if (run.status === 'completed') {
        const counts = formatImSelectionRunMeta(run);
        this.$store.commit('sbSuccess', counts || 'IM selection completed', { root: true });
      }
    },
    async refreshComputationRunStatus() {
      if (!this.projectId) return null;
      try {
        const data = await this.$store.dispatch('accruals/loadComputationRunStatus', {
          projectId: this.projectId,
        });
        if (isAccrualsComputationInProgress(data?.status)) {
          this.pollComputationUntilSettled(data?.computation_run_id);
        }
        return data;
      } catch (err) {
        console.log('[AccrualsConfiguration]: loadComputationRunStatus failed', err);
        return null;
      }
    },
    async pollComputationUntilSettled(runId) {
      const generation = this.runPollGeneration;
      this.computationRunPolling = true;
      try {
        const last = await pollAccrualsRun({
          fetchStatus: () => this.$store.dispatch('accruals/loadComputationRunStatus', {
            projectId: this.projectId,
            runId,
          }),
          isInProgress: isAccrualsComputationInProgress,
          shouldStop: () => generation !== this.runPollGeneration,
        });
        if (generation !== this.runPollGeneration) return last;
        this.notifyComputationSettled(last);
        return last;
      } catch (err) {
        console.log('[AccrualsConfiguration]: pollComputationUntilSettled failed', err);
        return null;
      } finally {
        if (generation === this.runPollGeneration) this.computationRunPolling = false;
      }
    },
    notifyComputationSettled(run) {
      if (isAccrualsComputationInProgress(run?.status) || !run?.status) return;
      if (run.status === 'failed') {
        this.$store.commit('sbError', run.error || 'Estimation run failed', { root: true });
        return;
      }
      if (run.status === 'completed') {
        const counts = formatComputationRunMeta(run);
        this.$store.commit('sbSuccess', counts || 'Estimation run completed', { root: true });
      }
    },
    goBack() {
      this.$nova.gotoLink({ path: ACCRUALS_ROUTES.home(this.projectId) });
    },
    onAddRuleFromEmpty() {
      if (this.isEstimationTab) {
        const nextRule = createEmptyEstimationRule({
          id: `__draft__${Date.now()}`,
          name: 'New rule',
        });
        this.estimationLocalDrafts = [nextRule];
        this.selectedEstimationRuleId = nextRule.id;
        this.$set(this.builderActiveByTab, ACCRUALS_CONFIG_TABS.ESTIMATION, true);
        return;
      }
      this.openBuilder();
    },
    onSelectEstimationRule(ruleId) {
      this.selectedEstimationRuleId = ruleId;
      this.$store.commit('accruals/setSelectedRuleId', ruleId);
    },
    onAddEstimationRule(rule) {
      const nextRule = normalizeEstimationRule(rule);
      this.upsertLocalDraft(nextRule);
      this.selectedEstimationRuleId = estimationRuleId(nextRule);
    },
    async onUpdateEstimationRules(nextRules) {
      this.estimationLocalDrafts = (nextRules || []).filter((rule) => this.isDraftEstimationRule(rule));
      const orderedIds = (nextRules || [])
        .filter((rule) => !this.isDraftEstimationRule(rule))
        .map((rule) => rule._id || rule.id)
        .filter(Boolean);
      if (!orderedIds.length) return;
      try {
        await this.$store.dispatch('accruals/reorderComputationRules', {
          projectId: this.projectId,
          orderedIds,
        });
      } catch (err) {
        console.log('[AccrualsConfiguration]: reorderComputationRules failed', err);
      }
    },
    onUpdateEstimationRuleDraft(rule) {
      const normalized = normalizeEstimationRule(rule);
      if (this.isDraftEstimationRule(normalized)) {
        this.upsertLocalDraft(normalized);
        return;
      }
      const id = estimationRuleId(normalized);
      if (!id) return;
      this.$set(this.estimationEditsById, id, normalized);
    },
    validateEstimationVendorScope(rule) {
      if (rule.applyOn === 'po') return true;
      const ids = rule.vendorIds || [];
      if (ids.includes(ESTIMATION_ALL_VENDORS_ID)) return true;
      if (ids.filter(Boolean).length) return true;
      this.$store.commit(
        'sbError',
        'Select All vendors or at least one vendor before saving',
        { root: true },
      );
      return false;
    },
    markEstimationRuleSaved(rule) {
      const id = estimationRuleId(rule);
      if (!id) return;
      this.removeLocalDraft(id);
      if (this.estimationEditsById[id]) this.$delete(this.estimationEditsById, id);
    },
    collectDirtyEstimationRules(extraRules = []) {
      const extras = Array.isArray(extraRules) ? extraRules : [extraRules];
      const merged = uniqueEstimationRules([
        ...(this.estimationLocalDrafts || []),
        ...Object.values(this.estimationEditsById || {}),
        ...extras.filter(Boolean),
      ]);
      return merged.sort(
        (a, b) => Number(a.execution_sequence || 0) - Number(b.execution_sequence || 0),
      );
    },
    async persistEstimationRule(rule, { confirm = false, silent = false, selectSaved = true } = {}) {
      if (!this.validateEstimationVendorScope(rule)) return null;
      const payload = uiRuleToApi(rule);
      const result = await this.$store.dispatch('accruals/saveComputationRule', {
        projectId: this.projectId,
        rule: payload,
        confirm,
        silent,
      });
      if (result && result.requires_confirmation) {
        this.pendingSaveKind = 'computation';
        this.pendingSaveRule = rule;
        this.pendingSaveAffectedCount = result.affected_count;
        this.saveConfirmDialog = true;
        return result;
      }
      if (result && result.rerun_required) {
        const count = result.affected_count;
        const lines = count == null
          ? 'Finalised estimates will be reset when you re-run.'
          : `${count} line${count === 1 ? '' : 's'} will be reset when you re-run.`;
        this.$store.commit('sbWarning', `Estimation rule saved. ${lines}`, { root: true });
      }
      const savedId = result && (result._id || result.id);
      if (savedId) {
        this.markEstimationRuleSaved(rule);
        if (selectSaved) {
          this.selectedEstimationRuleId = String(savedId);
          this.$store.commit('accruals/setSelectedRuleId', String(savedId));
        }
      }
      return result;
    },
    async persistEstimationRules(rules, { confirmFirst = false } = {}) {
      const originalSelected = this.selectedEstimationRuleId;
      const list = this.collectDirtyEstimationRules(rules);
      if (!list.length) return [];
      const silentAll = list.length > 1;
      const saved = [];
      for (let index = 0; index < list.length; index += 1) {
        const rule = list[index];
        const remaining = list.slice(index + 1);
        const result = await this.persistEstimationRule(rule, {
          confirm: confirmFirst && index === 0,
          silent: silentAll,
          selectSaved: false,
        });
        if (result && result.requires_confirmation) {
          this.pendingSaveQueue = remaining;
          return saved;
        }
        if (!result) {
          this.selectedEstimationRuleId = estimationRuleId(rule);
          this.pendingSaveQueue = remaining;
          return saved;
        }
        saved.push(result);
      }
      const stillSelected = originalSelected && this.estimationRules.some(
        (rule) => estimationRuleId(rule) === originalSelected,
      );
      if (stillSelected) {
        this.selectedEstimationRuleId = originalSelected;
        this.$store.commit('accruals/setSelectedRuleId', originalSelected);
      } else if (saved.length) {
        const lastId = String(saved[saved.length - 1]._id || saved[saved.length - 1].id || '');
        if (lastId) {
          this.selectedEstimationRuleId = lastId;
          this.$store.commit('accruals/setSelectedRuleId', lastId);
        }
      }
      if (silentAll) {
        this.$store.commit('sbSuccess', `Saved ${saved.length} estimation rules`, { root: true });
      }
      console.log('[AccrualsConfiguration]: persistEstimationRules', { count: saved.length });
      return saved;
    },
    async onSaveEstimationRule(rule) {
      try {
        await this.persistEstimationRules(rule);
      } catch (err) {
        console.log('[AccrualsConfiguration]: saveComputationRule failed', err);
      }
    },
    async onSaveEstimationRules(rules) {
      try {
        await this.persistEstimationRules(rules);
      } catch (err) {
        console.log('[AccrualsConfiguration]: saveComputationRules failed', err);
      }
    },
    async onConfirmSaveEstimationRule() {
      if (this.pendingSaveKind === 'im') {
        const action = this.pendingImAction;
        try {
          const saved = await this.saveInfoRequestRule({ confirm: true });
          this.saveConfirmDialog = false;
          this.pendingSaveKind = 'computation';
          this.pendingSaveAffectedCount = null;
          this.pendingImAction = '';
          if (!saved) return;
          if (action === 'save') this.imBuilderEditing = false;
          if (action === 'preview') await this.loadImPreview();
          if (action === 'selection') {
            await this.startImSelectionRun();
            return;
          }
          await this.refreshImSelectionStatus();
        } catch (err) {
          console.log('[AccrualsConfiguration]: confirm saveImRule failed', err);
        }
        return;
      }
      const rule = this.pendingSaveRule;
      const queue = this.pendingSaveQueue.slice();
      if (!rule) {
        this.saveConfirmDialog = false;
        this.pendingSaveQueue = [];
        return;
      }
      try {
        const confirmed = await this.persistEstimationRule(rule, {
          confirm: true,
          silent: queue.length > 0,
        });
        this.saveConfirmDialog = false;
        this.pendingSaveRule = null;
        this.pendingSaveAffectedCount = null;
        this.pendingSaveQueue = [];
        if (!confirmed || confirmed.requires_confirmation) return;
        if (queue.length) await this.persistEstimationRules(queue);
      } catch (err) {
        console.log('[AccrualsConfiguration]: confirm saveComputationRule failed', err);
      }
    },
    onCancelSaveConfirm() {
      this.saveConfirmDialog = false;
      this.pendingSaveRule = null;
      this.pendingSaveQueue = [];
      this.pendingSaveKind = 'computation';
      this.pendingImAction = '';
      this.pendingSaveAffectedCount = null;
    },
    async onDeleteEstimationRule(ruleId) {
      const id = String(ruleId || '');
      const isDraft = this.estimationLocalDrafts.some((rule) => estimationRuleId(rule) === id)
        || id.startsWith('__draft__')
        || id.startsWith('rule-');
      if (isDraft) {
        this.removeLocalDraft(id);
        const remaining = this.estimationRules.filter((rule) => estimationRuleId(rule) !== id);
        this.selectedEstimationRuleId = remaining[0] ? estimationRuleId(remaining[0]) : '';
        return;
      }
      try {
        await this.$store.dispatch('accruals/deleteComputationRule', {
          projectId: this.projectId,
          ruleId: id,
        });
        const remaining = this.estimationRules.filter((rule) => estimationRuleId(rule) !== id);
        this.selectedEstimationRuleId = remaining[0] ? estimationRuleId(remaining[0]) : '';
      } catch (err) {
        console.log('[AccrualsConfiguration]: deleteComputationRule failed', err);
      }
    },
    onCancelEstimationRuleEdit() {
      const selected = this.selectedEstimationRuleId;
      if (selected && this.estimationEditsById[selected]) {
        this.$delete(this.estimationEditsById, selected);
      }
      if (selected && (selected.startsWith('__draft__') || selected.startsWith('rule-'))) {
        this.removeLocalDraft(selected);
        const remaining = this.estimationRules;
        this.selectedEstimationRuleId = remaining[0] ? estimationRuleId(remaining[0]) : '';
      }
      console.log('[AccrualsConfiguration]:', { action: 'cancel-estimation-rule-edit' });
    },
    async onRunEstimates() {
      this.computationRunStarting = true;
      try {
        const result = await this.$store.dispatch('accruals/startComputationRun', {
          projectId: this.projectId,
        });
        this.computationRunStarting = false;
        if (result?.skipped) {
          await this.pollComputationUntilSettled();
          return;
        }
        await this.pollComputationUntilSettled(result?.computation_run_id);
      } catch (err) {
        console.log('[AccrualsConfiguration]: startComputationRun failed', err);
        this.computationRunStarting = false;
      }
    },
    onBuilderEdit() {
      this.builderEditing = true;
    },
    onBuilderCancel() {
      this.builderEditing = false;
      this.builderDraft = null;
      this.resetBuilderEditState();
    },
    onBuilderSave() {
      this.builderEditing = false;
      this.resetBuilderEditState();
    },
    updateRuleMeta(meta) {
      this.setRuleMetaForTab(this.activeTab, meta, this.activeConditions.length);
    },
    openBuilder() {
      this.$set(this.builderActiveByTab, this.activeTab, true);
      if (this.isInfoRequestTab) {
        if (!this.imGroups.length) {
          this.imGroups = createInitialImGroups();
          this.imGroupLogic = IM_GROUP_LOGIC.AND;
          this.imGroupConnectors = [];
          this.conditionErrors = {};
        }
        this.imBuilderEditing = true;
        return;
      }
      if (!this.activeConditions.length) {
        const initialConditions = createInitialBuilderConditions();
        this.builderDraft = {
          conditions: [],
          ruleMeta: createEmptyRuleMeta(0),
        };
        this.$set(this.rulesByTab, this.activeTab, initialConditions);
        this.setRuleMetaForTab(
          this.activeTab,
          createEmptyRuleMeta(initialConditions.length),
          initialConditions.length,
        );
        this.builderEditing = true;
        this.conditionErrors = {};
      }
    },
    addImGroup() {
      const next = this.imGroups.slice();
      const group = createEmptyImGroup(next.length);
      next.push(group);
      this.imGroups = next;
      this.imGroupConnectors = this.imGroupConnectors.concat([this.imGroupLogic || IM_GROUP_LOGIC.AND]);
      this.syncImGroupConnectors(this.imGroupLogic);
      this.conditionErrors = {};
    },
    async removeImGroup(groupId) {
      if (this.imGroups.length <= 1) return;
      this.imGroups = this.imGroups.filter((g) => g.id !== groupId);
      this.syncImGroupConnectors(this.imGroupLogic);
      if (this.editingGroupId === groupId) this.resetRowEditState();
      if (!this.imBuilderEditing && this.imValidation.valid) {
        await this.saveInfoRequestRule();
      }
    },
    setImGroupLogic({ groupId, logic }) {
      this.imGroups = this.imGroups.map((group) => (
        group.id === groupId
          ? { ...group, logic: logic === 'OR' ? IM_GROUP_LOGIC.OR : IM_GROUP_LOGIC.AND }
          : group
      ));
    },
    setImGroupConnector(payload) {
      const index = typeof payload === 'object' ? payload.index : 0;
      const logic = typeof payload === 'object' ? payload.logic : payload;
      const nextLogic = logic === 'OR' ? IM_GROUP_LOGIC.OR : IM_GROUP_LOGIC.AND;
      this.syncImGroupConnectors(this.imGroupLogic);
      const next = this.imGroupConnectors.slice();
      if (index < 0 || index >= next.length) return;
      next.splice(index, 1, nextLogic);
      this.imGroupConnectors = next;
      this.imGroupLogic = next.every((c) => c === next[0]) ? next[0] : next[0];
    },
    addImCondition(groupId) {
      this.imGroups = this.imGroups.map((group) => {
        if (group.id !== groupId) return group;
        const conditions = group.conditions.slice();
        conditions.push(createEmptyCondition(conditions.length));
        return { ...group, conditions };
      });
      this.conditionErrors = {};
    },
    updateImCondition({ groupId, index, condition }) {
      this.imGroups = this.imGroups.map((group) => {
        if (group.id !== groupId) return group;
        const conditions = group.conditions.slice();
        conditions.splice(index, 1, condition);
        return { ...group, conditions };
      });
      const key = `${groupId}:${index}`;
      if (this.conditionErrors[key]) {
        const { errors } = validateCondition(condition);
        this.$set(this.conditionErrors, key, errors);
      }
    },
    async deleteImCondition({ groupId, index }) {
      const group = this.imGroups.find((g) => g.id === groupId);
      if (!group) return;
      const nextConditions = group.conditions.slice();
      nextConditions.splice(index, 1);

      if (!nextConditions.length) {
        if (this.imGroups.length === 1) {
          this.imGroups = [];
          this.$set(this.builderActiveByTab, ACCRUALS_CONFIG_TABS.INFO_REQUEST, false);
          this.imBuilderEditing = false;
          this.resetRowEditState();
          if (this.imRule?.version > 0) {
            await this.disableInfoRequestRule();
          }
          return;
        }
        this.imGroups = this.imGroups.filter((g) => g.id !== groupId);
      } else {
        this.imGroups = this.imGroups.map((g) => (
          g.id === groupId ? { ...g, conditions: nextConditions } : g
        ));
      }

      if (this.editingGroupId === groupId) {
        if (this.editingIndex === index) {
          this.resetRowEditState();
        } else if (this.editingIndex > index) {
          this.editingIndex -= 1;
        }
      }
      this.conditionErrors = {};
      if (!this.imBuilderEditing && this.imValidation.valid) {
        await this.saveInfoRequestRule();
      }
    },
    onEditImCondition({ groupId, index }) {
      this.editingGroupId = groupId;
      this.editingIndex = index;
      const key = `${groupId}:${index}`;
      if (this.conditionErrors[key]) {
        this.$set(this.conditionErrors, key, { field: '', operator: '', value: '' });
      }
    },
    async onConfirmImCondition({ groupId, index }) {
      const group = this.imGroups.find((g) => g.id === groupId);
      const condition = group?.conditions?.[index];
      if (!condition) return;
      const { valid, errors } = validateCondition(condition);
      this.$set(this.conditionErrors, `${groupId}:${index}`, errors);
      if (!valid) return;
      if (!this.collectImGroupErrors()) {
        this.$store.commit('sbError', CONDITIONS_INCOMPLETE_MESSAGE, { root: true });
        return;
      }
      if (this.imBuilderEditing) {
        this.resetRowEditState();
        return;
      }
      const saved = await this.saveInfoRequestRule();
      if (saved) {
        this.resetRowEditState();
      }
    },
    addCondition() {
      if (!this.builderEditing) return;
      const next = this.activeConditions.slice();
      next.push(createEmptyCondition(next.length));
      this.$set(this.rulesByTab, this.activeTab, next);
      this.syncRuleMetaLength(this.activeTab);
      this.conditionErrors = {};
    },
    updateCondition({ index, condition }) {
      const next = this.activeConditions.slice();
      next.splice(index, 1, condition);
      this.$set(this.rulesByTab, this.activeTab, next);
      if (this.conditionErrors[index]) {
        const { errors } = validateCondition(condition);
        this.$set(this.conditionErrors, index, errors);
      }
    },
    deleteCondition(index) {
      const next = this.activeConditions.slice();
      next.splice(index, 1);
      this.$set(this.rulesByTab, this.activeTab, next);
      this.syncRuleMetaLength(this.activeTab);
      this.conditionErrors = {};
      if (!next.length) {
        this.$set(this.builderActiveByTab, this.activeTab, false);
      }
    },
    onEditCondition(index) {
      this.editingIndex = index;
      if (this.conditionErrors[index]) {
        this.$set(this.conditionErrors, index, { field: '', operator: '', value: '' });
      }
    },
    onConfirmCondition(index) {
      const { valid, errors } = validateCondition(this.activeConditions[index]);
      this.$set(this.conditionErrors, index, errors);
      if (!valid) return;
      const all = validateConditions(this.activeConditions);
      this.conditionErrors = all.errorsByIndex;
      if (!all.valid) {
        this.$store.commit('sbError', CONDITIONS_INCOMPLETE_MESSAGE, { root: true });
        return;
      }
      this.resetRowEditState();
    },
    onImport() {
      this.$store.commit(
        'sbInfo',
        'Import rules is not wired yet — awaiting product/API flow.',
        { root: true },
      );
    },
    onImBuilderEdit() {
      this.imBuilderEditing = true;
      this.resetRowEditState();
    },
    async onImBuilderCancel() {
      this.resetRowEditState();
      if (this.imRule?.version > 0) {
        await this.fetchInfoRequestRule();
        this.imBuilderEditing = false;
        return;
      }
      this.imGroups = [];
      this.imGroupLogic = IM_GROUP_LOGIC.AND;
      this.imGroupConnectors = [];
      this.$set(this.builderActiveByTab, ACCRUALS_CONFIG_TABS.INFO_REQUEST, false);
      this.imBuilderEditing = false;
    },
    async onImBuilderSave() {
      const saved = await this.saveInfoRequestRule();
      if (!saved) {
        if (this.saveConfirmDialog) this.pendingImAction = 'save';
        return;
      }
      this.imBuilderEditing = false;
      this.$store.commit('sbSuccess', 'Info request rules saved', { root: true });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsConfiguration.scss';
</style>
